import * as React from 'react';
import { useContext } from 'react';

import { AplixPage, BarcodeListener } from '../../../../../../components';
import { EditDetailContext } from '../../EditDetailContext';
import Box from '@material-ui/core/Box';
import Table from '@material-ui/core/Table';
import { TableHead } from '@material-ui/core';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import EditIcon from '@material-ui/icons/Edit';
import Button from '@material-ui/core/Button';
import DeleteIcon from '@material-ui/icons/Delete';
import Grid from '@material-ui/core/Grid';
import { useService } from '../../../../../../ioc/hook/useService';
import { IEditDetailStoreQueries } from '../../../../logic/IEditDetailStoreQueries';
import APLIX_SERVICE_IDENTIFIER from '../../../../../../ioc_constants';
import { getService } from '../../../../../../ioc_config';
import { IScannerService } from '../../../../../../core/scanner/IScannerService';

interface IStyles {
    listPlacesTableContainer: string;
}

const styles: IStyles = require('./ListPlaces.less');

interface IProps {
    goBack: () => void;
    openEditor: (placeId?: string) => void;
    submit: () => void;
    deletePlace: (placeId: string) => void;
}

export const ListPlaces: React.FunctionComponent<IProps> = ({
    openEditor,
    goBack,
    submit,
    deletePlace,
}) => {
    const [detailStore] = useContext(EditDetailContext);
    const { articleNumber, places, unit, currentQuantity } = detailStore;

    const detailStoreQueries = useService<IEditDetailStoreQueries>(
        APLIX_SERVICE_IDENTIFIER.IEDITDETAILSTOREQUERIES
    );
    const scannerService = getService<IScannerService>(APLIX_SERVICE_IDENTIFIER.ISCANNERSERVICE);

    const placesAmount = detailStoreQueries.getPlacesAmount(detailStore);
    let remainingAmount = detailStoreQueries.roundAmount(
        detailStore,
        detailStore.currentQuantity - placesAmount
    );
    let amountMatches = detailStore.currentQuantity === placesAmount;

    // #10057 automatic distribution to places if just one place and currentQuantity less than amount of place
    if (places.length === 1 && remainingAmount < 0) {
        places[0].amount = detailStore.currentQuantity;
        remainingAmount = 0;
        amountMatches = true;
    }

    const onScan = (barcode: string) => {
        return openEditor(barcode);
    };

    return (
        <AplixPage
            showBackButton
            pageTitle={`Artikel "${articleNumber}": Lagerplätze wählen`}
            onBackClick={goBack}
        >
            <BarcodeListener onScan={onScan} scannerService={scannerService} />
            <Box
                display={'flex'}
                flexGrow={1}
                className={styles.listPlacesTableContainer}
                flexDirection={'column'}
                width={1}
                style={{ overflowY: 'auto' }}
            >
                <Table>
                    <TableHead key={'header'}>
                        <TableRow>
                            <TableCell size="small">Lagerplatz</TableCell>
                            <TableCell size="small">Eingelagert</TableCell>
                            <TableCell size="small" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {places.map((place) => (
                            <TableRow key={place.placeId} onClick={() => openEditor(place.placeId)}>
                                <TableCell>{place.placeId}</TableCell>
                                <TableCell>
                                    {place.amount} {unit}
                                </TableCell>
                                <TableCell align={'right'}>
                                    <Box display={'flex'} justifyContent={'flex-end'}>
                                        <Button
                                            variant="contained"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                deletePlace(place.placeId);
                                            }}
                                        >
                                            <DeleteIcon />
                                        </Button>
                                        <Box ml={1}>
                                            <Button variant="contained">
                                                <EditIcon />
                                            </Button>
                                        </Box>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
            <Box mt={1}>
                {!amountMatches && (
                    <Box mb={1} color={'text.hint'} textAlign={'right'}>
                        Gesamtmenge aller Lagerplätze:{' '}
                        <strong>
                            {placesAmount} {unit}
                        </strong>
                        . Soll:{' '}
                        <strong>
                            {currentQuantity} {unit}
                        </strong>
                        . Differenz:{' '}
                        <strong>
                            {remainingAmount} {unit}
                        </strong>
                    </Box>
                )}
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Button fullWidth variant={'contained'} onClick={() => openEditor()}>
                            Lagerplatz hinzufügen
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant={'contained'}
                            color={'primary'}
                            onClick={submit}
                            disabled={!amountMatches}
                            id={'edit-places-submit'}
                        >
                            Weiter
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </AplixPage>
    );
};
